import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import DeviceDetail from "./pages/DeviceDetail";
import Login from "./pages/Login";
import ModelCompliance from "./pages/ModelCompliance";
import UserManagement from "./pages/UserManagement";
import Deployment from "./pages/Deployment";
import RequestAccess from "./pages/RequestAccess";
import Register from "./pages/Register";
import FeatureRequests from "./pages/FeatureRequests";
import ScheduledUpdates from "./pages/ScheduledUpdates";
import TriggerWatchPackages from "./pages/TriggerWatchPackages";
import TriggerWatchDeployments from "./pages/TriggerWatchDeployments";
import TriggerWatchDeviceGroups from "./pages/TriggerWatchDeviceGroups";
import TriggerWatchDashboard from "./pages/TriggerWatchDashboard";
import MFASetup from "./pages/MFASetup";
import useInactivityTimeout from "./hooks/useInactivityTimeout";
import TrialBanner from "./components/TrialBanner";
import PortalHome from "./pages/PortalHome";

function ProtectedRoute({ children }) {
  const token = sessionStorage.getItem("token");
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function InactivityWrapper({ children }) {
  const { showWarning, countdown, stayLoggedIn } = useInactivityTimeout();

  return (
    <>
      <TrialBanner />
      {children}
      {showWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-yellow-700 rounded-xl p-8 max-w-md w-full mx-4 text-center">
            <div className="text-yellow-400 text-4xl mb-4">⚠️</div>
            <h2 className="text-white text-xl font-bold mb-2">Session Expiring Soon</h2>
            <p className="text-gray-400 mb-6">
              You've been inactive for a while. You'll be logged out in{" "}
              <span className="text-yellow-400 font-bold">
                {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, "0")}
              </span>
            </p>
            <button
              onClick={stayLoggedIn}
              className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg transition-colors font-medium"
            >
              Stay Logged In
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function AppRoutes() {
  const token = sessionStorage.getItem("token");

  return (
    <InactivityWrapper>
      <Routes>
        <Route path="/login" element={<Login />} />

        <Route path="/portal" element={
          <ProtectedRoute><PortalHome /></ProtectedRoute>
        } />

        <Route path="/" element={
          <ProtectedRoute><Dashboard /></ProtectedRoute>
        } />

        <Route path="/devices/:deviceId" element={
          <ProtectedRoute><DeviceDetail /></ProtectedRoute>
        } />

        <Route path="/compliance/:status" element={
          <ProtectedRoute><ModelCompliance /></ProtectedRoute>
        } />

        <Route path="/users" element={
          <ProtectedRoute><UserManagement /></ProtectedRoute>
        } />

        <Route path="/deployment" element={
          <ProtectedRoute><Deployment /></ProtectedRoute>
        } />

        <Route path="/feature-requests" element={
          <ProtectedRoute><FeatureRequests /></ProtectedRoute>
        } />

        <Route path="/scheduled-updates" element={
          <ProtectedRoute><ScheduledUpdates /></ProtectedRoute>
        } />

        <Route path="/triggerwatch/packages" element={
          <ProtectedRoute><TriggerWatchPackages /></ProtectedRoute>
        } />

        <Route path="/triggerwatch/deployments" element={
          <ProtectedRoute><TriggerWatchDeployments /></ProtectedRoute>
        } />

        <Route path="/triggerwatch/device-groups" element={
          <ProtectedRoute><TriggerWatchDeviceGroups /></ProtectedRoute>
        } />

        <Route path="/triggerwatch" element={
          <ProtectedRoute><TriggerWatchDashboard /></ProtectedRoute>
        } />

        <Route path="/mfa-setup" element={
          <ProtectedRoute><MFASetup /></ProtectedRoute>
        } />
        <Route path="/request-access" element={<RequestAccess />} />
        <Route path="/register" element={<Register />} />
      </Routes>
    </InactivityWrapper>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;